/**
 * Copyright (c) 2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { __awaiter, __generator } from "tslib";
import { Column, Table } from '../../mol-data/db';
import { getElementFromAtomicNumber } from '../../mol-model/structure/model/types';
import { Task } from '../../mol-task';
import { createModels } from './basic/parser';
import { BasicSchema, createBasic } from './basic/schema';
import { ComponentBuilder } from './common/component';
import { EntityBuilder } from './common/entity';

function getModels(cube, ctx) {
  return __awaiter(this, void 0, void 0, function () {
    var atoms, MOL, A, type_symbol, seq_id, atom_site, entityBuilder, componentBuilder, basics;
    return __generator(this, function (_a) {
      switch (_a.label) {
        case 0:
          atoms = cube.atoms;
          MOL = Column.ofConst('MOL', cube.atoms.count, Column.Schema.str);
          A = Column.ofConst('A', cube.atoms.count, Column.Schema.str);
          type_symbol = Column.ofArray({
            array: Column.mapToArray(atoms.number, function (n) {
              return getElementFromAtomicNumber(n);
            }),
            schema: Column.Schema.Aliased(Column.Schema.str)
          });
          seq_id = Column.ofConst(1, atoms.count, Column.Schema.int);
          atom_site = Table.ofPartialColumns(BasicSchema.atom_site, {
            auth_asym_id: A,
            auth_atom_id: type_symbol,
            auth_comp_id: MOL,
            auth_seq_id: seq_id,
            Cartn_x: Column.asArrayColumn(atoms.x, Float32Array),
            Cartn_y: Column.asArrayColumn(atoms.y, Float32Array),
            Cartn_z: Column.asArrayColumn(atoms.z, Float32Array),
            id: Column.range(0, atoms.count - 1),
            label_asym_id: A,
            label_atom_id: type_symbol,
            label_comp_id: MOL,
            label_seq_id: seq_id,
            label_entity_id: Column.ofConst('1', atoms.count, Column.Schema.str),
            occupancy: Column.ofConst(1, atoms.count, Column.Schema.float),
            type_symbol: type_symbol,
            pdbx_PDB_model_num: Column.ofConst(1, atoms.count, Column.Schema.int)
          }, atoms.count);
          entityBuilder = new EntityBuilder();
          entityBuilder.setNames([['MOL', 'Unknown Entity']]);
          entityBuilder.getEntityId('MOL', 0
          /* Unknown */
          , 'A');
          componentBuilder = new ComponentBuilder(seq_id, type_symbol);
          componentBuilder.setNames([['MOL', 'Unknown Molecule']]);
          componentBuilder.add('MOL', 0);
          basics = createBasic({
            entity: entityBuilder.getEntityTable(),
            chem_comp: componentBuilder.getChemCompTable(),
            atom_site: atom_site
          });
          return [4
          /*yield*/
          , createModels(basics, MolFormat.create(cube), ctx)];

        case 1:
          return [2
          /*return*/
          , _a.sent()];
      }
    });
  });
}

var MolFormat;

(function (MolFormat) {
  function is(x) {
    return (x === null || x === void 0 ? void 0 : x.kind) === 'cube';
  }

  MolFormat.is = is;

  function create(cube) {
    return {
      kind: 'cube',
      name: cube.header.comment1,
      data: cube
    };
  }

  MolFormat.create = create;
})(MolFormat || (MolFormat = {}));

export function trajectoryFromCube(cube) {
  return Task.create('Parse Cube', function (ctx) {
    return getModels(cube, ctx);
  });
}